import {
  CASE_PLAN_ACCESS_PERMISSIONS,
  REENTRY_PLAN_FEATURE,
} from '../constants/reentry-planning.constants';

const LAST_REFRESHED_TIME_OPTIONS = {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
};

export const formatLastRefreshedText = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  return `Last Refreshed ${timestamp.toLocaleTimeString('en-US', LAST_REFRESHED_TIME_OPTIONS)}`;
};

const normalizePermissionName = (permissionName) => permissionName?.toLowerCase() ?? '';

const CASE_PLAN_ACCESS_PERMISSIONS_SET = new Set(
  CASE_PLAN_ACCESS_PERMISSIONS.map((permission) => permission.toLowerCase()),
);

export const hasCasePlanAccess = (permissions) =>
  permissions?.some((permission) =>
    CASE_PLAN_ACCESS_PERMISSIONS_SET.has(normalizePermissionName(permission.permissionName)),
  );

export const isReentryPlanFeatureEnabled = (locationFeatures) =>
  locationFeatures?.find((feature) => feature.featureName === REENTRY_PLAN_FEATURE)?.status === true;

export const createViewPlanClickHandler = (onViewPlan, plan) => () => {
  onViewPlan(plan);
};

export const REENTRY_PLANS_LOCATION_COLUMN = 'location';

const FACILITY_LOCATION_TYPE_ID = 3;
const HOUSING_UNIT_LOCATION_TYPE_ID = 15;

const compareByLocationName = (a, b) =>
  String(a.locationName).localeCompare(String(b.locationName));

const pickFacilitiesSorted = (locations) =>
  locations
    .filter((item) => item.locationTypeId === FACILITY_LOCATION_TYPE_ID)
    .sort(compareByLocationName);

const pickHousingUnitsForFacility = (locations, facility) =>
  locations
    .filter(
      (item) =>
        item.parentLocationId === facility.locationId &&
        item.locationTypeId === HOUSING_UNIT_LOCATION_TYPE_ID,
    )
    .sort(compareByLocationName);

const buildFacilityWithHousingUnits = (locations, facility) => [
  facility,
  ...pickHousingUnitsForFacility(locations, facility),
];

const toNonEmptyLocationName = (item) => {
  const name = item?.locationName;
  return name != null && String(name).trim() !== '' ? String(name) : null;
};

export const getLocationFilterOptions = (locations) => {
  if (!Array.isArray(locations) || locations.length === 0) {
    return [];
  }
  return pickFacilitiesSorted(locations)
    .flatMap((facility) => buildFacilityWithHousingUnits(locations, facility))
    .map(toNonEmptyLocationName)
    .filter(Boolean);
};

export const mapLocationFilterToLocationIds = (filters, locations = []) => {
  const filter = Array.isArray(filters)
    ? filters.find((item) => item.columnName === REENTRY_PLANS_LOCATION_COLUMN)
    : null;

  if (!filter?.value) {
    return undefined;
  }

  const locationNames = String(filter.value)
    .split(',')
    .map((name) => name.trim())
    .filter(Boolean);

  if (locationNames.length === 0) {
    return undefined;
  }

  const locationIds = locations
    .filter(
      (item) =>
        locationNames.includes(item.locationName) &&
        (item.locationTypeId === FACILITY_LOCATION_TYPE_ID ||
          item.locationTypeId === HOUSING_UNIT_LOCATION_TYPE_ID),
    )
    .map((item) => item.locationId);

  return locationIds.length > 0 ? locationIds.join(',') : undefined;
};
